import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { useOrganization } from "@/api/hooks/organizations";
import DocumentsSection from "@/components/documents/DocumentsSection";
import {
  useCreateOrgContact,
  useDeleteOrgContact,
  useOrgContacts,
  useUpdateOrgContact,
} from "@/api/hooks/organizationContacts";
import {
  ORGANIZATION_CONTACT_ROLES,
  ORGANIZATION_CONTACT_ROLE_LABELS,
} from "@/api/labels";
import type {
  OrganizationContact,
  OrganizationContactRole,
} from "@/api/types";

interface ContactFormState {
  role: OrganizationContactRole;
  name: string;
  email: string;
  phone: string;
  title: string;
  notes: string;
}

const EMPTY_CONTACT: ContactFormState = {
  role: "executive_sponsor",
  name: "",
  email: "",
  phone: "",
  title: "",
  notes: "",
};

function fromContact(c: OrganizationContact): ContactFormState {
  return {
    role: c.role,
    name: c.name,
    email: c.email ?? "",
    phone: c.phone ?? "",
    title: c.title ?? "",
    notes: c.notes ?? "",
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function LabeledValue({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: org, isLoading, error } = useOrganization(id);
  const { data: contacts } = useOrgContacts(id);

  const create = useCreateOrgContact(id ?? "");
  const remove = useDeleteOrgContact(id ?? "");

  const [editing, setEditing] = useState<OrganizationContact | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ContactFormState>(EMPTY_CONTACT);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = useUpdateOrgContact(editing?.id ?? "", id);

  if (isLoading) return <CircularProgress />;
  if (error || !org)
    return (
      <Alert severity="error">{error?.message ?? "Organization not found."}</Alert>
    );

  // v1 deliberately doesn't render an embedded Sites-under-Org table here —
  // SiteListRow doesn't expose organization_id, and the SitesList aggregate
  // query isn't org-aware. Future: add /api/organizations/{id}/sites or filter
  // /api/sites by organization_id. The site_count on the list view + the
  // "Part of [Org]" link from each Site detail covers v1 navigation.

  const openCreate = () => {
    setForm(EMPTY_CONTACT);
    setSubmitError(null);
    setCreating(true);
  };
  const openEdit = (c: OrganizationContact) => {
    setForm(fromContact(c));
    setSubmitError(null);
    setEditing(c);
  };
  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const submit = async () => {
    setSubmitError(null);
    const payload = {
      role: form.role,
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      title: form.title.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) await update.mutateAsync(payload);
      else await create.mutateAsync(payload);
      close();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5">{org.name}</Typography>
          {org.display_id && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            >
              {org.display_id}
            </Typography>
          )}
        </Box>
        <Link to={`/clinops/sites/organizations/${org.id}/edit`} style={{ textDecoration: "none" }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
        </Link>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Master agreement
          </Typography>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid size={{ xs: 6, md: 4 }}>
              <LabeledValue label="MSA reference">
                {org.master_msa_reference ?? "—"}
              </LabeledValue>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <LabeledValue label="MSA signed">
                {formatDate(org.master_msa_signed_at)}
              </LabeledValue>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <LabeledValue label="Outreach started">
                {formatDate(org.outreach_started_at)}
              </LabeledValue>
            </Grid>
            {org.warm_intro_source && (
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Warm intro source">
                  {org.warm_intro_source}
                </LabeledValue>
              </Grid>
            )}
            {org.prior_relationship_notes && (
              <Grid size={{ xs: 12 }}>
                <LabeledValue label="Prior relationship notes">
                  {org.prior_relationship_notes}
                </LabeledValue>
              </Grid>
            )}
            {org.notes && (
              <Grid size={{ xs: 12 }}>
                <LabeledValue label="Notes">{org.notes}</LabeledValue>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Contacts
            </Typography>
            <Button size="small" startIcon={<Add />} onClick={openCreate}>
              Add contact
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(contacts ?? []).map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ORGANIZATION_CONTACT_ROLE_LABELS[c.role]}
                      />
                    </TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.title ?? "—"}</TableCell>
                    <TableCell>{c.email ?? "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(c)}
                        aria-label="Edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (confirm(`Delete ${c.name}?`)) {
                            await remove.mutateAsync(c.id);
                          }
                        }}
                        aria-label="Delete"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(contacts ?? []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 3, color: "text.secondary" }}
                    >
                      No contacts yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <DocumentsSection entityType="organization" entityId={org.id} />

      <Dialog open={creating || Boolean(editing)} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit contact" : "Add contact"}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={(e) =>
                setForm({
                  ...form,
                  role: e.target.value as OrganizationContactRole,
                })
              }
              required
            >
              {ORGANIZATION_CONTACT_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {ORGANIZATION_CONTACT_ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!form.name.trim()}
          >
            {editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
